/**
 * Invoices feature — UI-only presentation settings (three-bucket config, ADR 0002).
 *
 * Backed by ``InvoiceStatus`` / ``PaymentMethod`` literals from the
 * generated API types, which themselves derive from
 * ``app/features/billing/config/rules.py``.
 */

import type {
  InvoiceStatus,
  PaymentMethod,
  PaymentStatus,
} from '../api/billing';

export interface StatusOption<T extends string> {
  value: T;
  label: string;
  color: string;
}

export const INVOICE_STATUS_OPTIONS: readonly StatusOption<InvoiceStatus>[] = [
  { value: 'draft',          label: 'Draft',          color: '#9ca3af' },
  { value: 'issued',         label: 'Issued',         color: '#f59e0b' },
  { value: 'partially_paid', label: 'Partially Paid', color: '#3b82f6' },
  { value: 'paid',           label: 'Paid',           color: '#16a34a' },
  { value: 'void',           label: 'Void',           color: '#dc2626' },
] as const;

export const PAYMENT_STATUS_OPTIONS: readonly StatusOption<PaymentStatus>[] = [
  { value: 'active', label: 'Active', color: '#16a34a' },
  { value: 'voided', label: 'Voided', color: '#dc2626' },
] as const;

export interface PaymentMethodOption {
  value: PaymentMethod;
  label: string;
}

export const PAYMENT_METHOD_OPTIONS: readonly PaymentMethodOption[] = [
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'cash',          label: 'Cash' },
  { value: 'upi',           label: 'UPI' },
  { value: 'cheque',        label: 'Cheque' },
  { value: 'card_external', label: 'Card (external terminal)' },
  { value: 'other',         label: 'Other' },
] as const;

export const INVOICE_TABS = [
  { id: 'all',            label: 'All'             },
  { id: 'draft',          label: 'Drafts'          },
  { id: 'issued',         label: 'Issued'          },
  { id: 'partially_paid', label: 'Partially Paid'  },
  { id: 'paid',           label: 'Paid'            },
  { id: 'void',           label: 'Void'            },
] as const;

export type InvoiceTabId = (typeof INVOICE_TABS)[number]['id'];

/** Statuses where the operator is allowed to PATCH line items. */
export const INVOICE_EDITABLE_STATUSES: ReadonlySet<InvoiceStatus> = new Set(['draft']);

/** Statuses where ``POST /invoices/{id}/issue`` is meaningful. */
export const INVOICE_ISSUABLE_STATUSES: ReadonlySet<InvoiceStatus> = new Set(['draft']);

/** Statuses where ``POST /invoices/{id}/void`` is allowed. Mirrors backend
 * ``INVOICE_VOIDABLE_STATUSES`` — ``paid`` is excluded (refund/credit-note path). */
export const INVOICE_VOIDABLE_STATUSES: ReadonlySet<InvoiceStatus> = new Set([
  'draft',
  'issued',
  'partially_paid',
]);

/** Statuses where ``POST /payments`` accepts a payment against this invoice. */
export const INVOICE_PAYABLE_STATUSES: ReadonlySet<InvoiceStatus> = new Set([
  'issued',
  'partially_paid',
]);

export function invoiceStatusColor(status: InvoiceStatus | string): string {
  return INVOICE_STATUS_OPTIONS.find((o) => o.value === status)?.color ?? '#9ca3af';
}

export function invoiceStatusLabel(status: InvoiceStatus | string): string {
  return INVOICE_STATUS_OPTIONS.find((o) => o.value === status)?.label ?? status;
}

export function paymentMethodLabel(method: PaymentMethod | string): string {
  return PAYMENT_METHOD_OPTIONS.find((o) => o.value === method)?.label ?? method;
}

export function paymentStatusColor(status: PaymentStatus | string): string {
  return PAYMENT_STATUS_OPTIONS.find((o) => o.value === status)?.color ?? '#9ca3af';
}

/**
 * Format a minor-unit amount (paise / cents) as a display string.
 *
 * Defaults to INR with the currency symbol; pass ``currency`` for other
 * tenants. Uses ``Intl.NumberFormat`` for locale-aware grouping.
 */
export function formatMinorAmount(amount: number, currency = 'INR'): string {
  const major = amount / 100;
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(major);
  } catch {
    return `${currency} ${major.toFixed(2)}`;
  }
}

